import { useMemo } from 'react'

import { Scope } from '../enums/scope'
import { isAuthorized } from '../utils/isAuthorized'

interface ScopedFeatureAccess {
  isEnabled: boolean
  isLockedByUpsell: boolean
  shouldShowFeature: boolean
}

/**
 * Derives feature visibility from an active scope and an optional upsell scope:
 * - upsellScope omitted -> the feature is simply shown/hidden based on activeScope alone (isLockedByUpsell is always false, no upsell state exists)
 * - neither scope granted -> hide the feature entirely (shouldShowFeature: false)
 * - only the upsell scope granted -> show it locked behind an upsell paywall (shouldShowFeature: true, isLockedByUpsell: true)
 * - the active scope granted -> show the feature fully (shouldShowFeature: true, isEnabled: true)
 */
export const useScopedFeatureAccess = (activeScope: Scope, upsellScope?: Scope): ScopedFeatureAccess => {
  const isLockedByUpsell = upsellScope ? isAuthorized(upsellScope) : false
  const isEnabled = isAuthorized(activeScope)

  return useMemo(
    () => ({ isEnabled, isLockedByUpsell, shouldShowFeature: isEnabled || isLockedByUpsell }),
    [isEnabled, isLockedByUpsell],
  )
}
